import { Timestamp } from 'firebase/firestore'; // Import Timestamp for better type safety
import type {
  RepairRequestFulfillmentSnapshot,
  ServiceProviderFulfillmentConfig,
} from '../shared/fulfillment';

export enum UserRole {
  CLIENT = 'client',
  SERVICE_ADMIN = 'serviceAdmin',
  ADMIN = 'admin',
}

export enum UserStatus { // Define UserStatus enum for clarity
  PENDING = 'pending',
  PENDING_VERIFICATION = 'pending_verification',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  REJECTED = 'rejected',
}

export type ServiceVerificationStatus =
  | 'profile_incomplete'
  | 'pending_approval'
  | 'approved'
  | 'changes_requested';

export type BusinessHoursDayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface BusinessHoursDay {
  enabled: boolean;
  open: string | null;
  close: string | null;
}

export type BusinessHoursWeekly = Record<BusinessHoursDayKey, BusinessHoursDay>;

export interface BusinessHours {
  timezone: 'Europe/Warsaw';
  weekly: BusinessHoursWeekly;
  updatedAt?: Timestamp | Date | any | null;
}

export type ServiceAvailabilityOverrideType = 'vacation' | 'technical_break';

export interface ServiceAvailabilityOverride<TTimestamp = Timestamp | Date | any> {
  enabled: boolean;
  type: ServiceAvailabilityOverrideType;
  message?: string;
  startsAt?: TTimestamp | null;
  endsAt?: TTimestamp | null;
  updatedAt?: TTimestamp | null;
  updatedBy?: string;
}

export type ServiceProviderProfileImageVariantName = 'full' | 'thumb';

export interface ServiceProviderProfileImageVariantInput {
  storagePath: string;
  contentType: string;
  bytes: number;
  width: number;
  height: number;
}

export interface ServiceProviderProfileImageVariant
  extends ServiceProviderProfileImageVariantInput {
  url: string;
}

export interface ServiceProviderProfileImageMutation {
  assetId: string;
  sourceFileName: string | null;
  sourceMimeType: string;
  sourceBytes: number;
  sourceWidth: number;
  sourceHeight: number;
  full: ServiceProviderProfileImageVariantInput;
  thumb: ServiceProviderProfileImageVariantInput;
}

export interface ServiceProviderProfileImage extends ServiceProviderProfileImageMutation {
  createdAt?: Timestamp | Date | any | null;
  full: ServiceProviderProfileImageVariant;
  thumb: ServiceProviderProfileImageVariant;
}

export enum RepairStatus {
  NEW = 'new',
  PENDING_QUOTE = 'pendingQuote',
  QUOTED = 'quoted',
  ACCEPTED = 'accepted',
  IN_PROGRESS = 'inProgress',
  PENDING_PAYMENT = 'pendingPayment',
  COMPLETED = 'completed',
  CANCELLED_BY_CLIENT = 'cancelledByClient',
  CANCELLED_BY_SERVICE = 'cancelledByService',
  REJECTED = 'rejected', // Note: Same name as UserStatus.REJECTED, ensure context clarifies
  PRELIMINARY_QUOTED = 'preliminaryQuoted',
  AWAITING_FULFILLMENT = 'awaitingFulfillment',
  DIAGNOSIS_IN_PROGRESS = 'diagnosisInProgress',
  FINAL_QUOTE_READY = 'finalQuoteReady',
  FINAL_QUOTE_ACCEPTED = 'finalQuoteAccepted',
  FINAL_QUOTE_REJECTED = 'finalQuoteRejected',
  REPAIR_IN_PROGRESS = 'repairInProgress',
  REPAIR_COMPLETED = 'repairCompleted',
  AWAITING_CASH_SETTLEMENT = 'awaitingCashSettlement',
  PRELIMINARY_REJECTED = 'preliminaryRejected',
}

export type StatusFilter = RepairStatus | 'active' | 'all';

export type RepairWorkflowVersion = 1 | 2;
export type RepairRequestFlowType = 'repair' | 'service';

export interface PreliminaryEstimate<TTimestamp = Timestamp | Date | any> {
  diagnosisFee?: number;
  pickupOrTravelFee?: number;
  priceRangeMin?: number;
  priceRangeMax?: number;
  comment?: string;
  createdAt?: TTimestamp;
  createdBy?: string;
  acceptedAt?: TTimestamp;
  acceptedBy?: string;
  rejectedAt?: TTimestamp;
  rejectedBy?: string;
}

export interface RepairRequestPriorityRequest<TTimestamp = Timestamp | Date | any> {
  requested: boolean;
  acceptsPotentialExtraFee: boolean;
  note?: string;
  createdAt?: TTimestamp;
}

export type RepairRequestServiceScheduleSource = 'serviceAdminPlanner';
export type RepairRequestServiceScheduleTimezone = 'Europe/Warsaw';

export interface RepairRequestServiceScheduleScheduled<TTimestamp = Timestamp | Date | any> {
  status: 'scheduled';
  scheduledStartAt: TTimestamp;
  scheduledEndAt?: TTimestamp;
  timezone: RepairRequestServiceScheduleTimezone;
  note?: string;
  createdAt: TTimestamp;
  createdByUid: string;
  updatedAt: TTimestamp;
  updatedByUid: string;
  source: RepairRequestServiceScheduleSource;
}

export interface RepairRequestServiceScheduleCleared<TTimestamp = Timestamp | Date | any> {
  status: 'cleared';
  timezone: RepairRequestServiceScheduleTimezone;
  clearedAt: TTimestamp;
  clearedByUid: string;
  updatedAt: TTimestamp;
  updatedByUid: string;
  source: RepairRequestServiceScheduleSource;
}

export type RepairRequestServiceSchedule<TTimestamp = Timestamp | Date | any> =
  | RepairRequestServiceScheduleScheduled<TTimestamp>
  | RepairRequestServiceScheduleCleared<TTimestamp>;

export type FinalRepairQuoteLineItemType = 'parts' | 'labor' | 'other';

export interface FinalRepairQuoteLineItem {
  type: FinalRepairQuoteLineItemType;
  label: string;
  amount: number;
  quantity?: number;
  notes?: string;
}

export type FinalRepairQuoteStatus = 'pendingClientDecision' | 'accepted' | 'rejected';

export interface RepairDiagnosis<TTimestamp = Timestamp | Date | any> {
  description?: string;
  cause?: string;
  notes?: string;
  createdAt?: TTimestamp;
  createdByUserId?: string;
}

export interface FinalRepairQuote<TTimestamp = Timestamp | Date | any> {
  amount?: number;
  currency?: 'PLN';
  description?: string;
  lineItems?: FinalRepairQuoteLineItem[];
  status?: FinalRepairQuoteStatus;
  createdAt?: TTimestamp;
  createdByUserId?: string;
  respondedAt?: TTimestamp;
  respondedByUserId?: string;
  rejectionReason?: string;
}

export type RepairHandoverEventType =
  | 'customerDropoffReceived'
  | 'servicePickupCompleted'
  | 'onsiteVisitStarted'
  | 'onsiteVisitCompleted'
  | 'releaseConfirmed';

export interface RepairHandoverEvent<TTimestamp = Timestamp | Date | any> {
  type: RepairHandoverEventType;
  mode?: 'customerDropoff' | 'servicePickup' | 'onsiteService';
  actorUserId?: string;
  actorRole?: 'serviceAdmin' | 'client' | 'service' | string;
  createdAt?: TTimestamp;
  occurredAt?: TTimestamp;
  confirmedBy?: string;
  note?: string;
}

export type CashSettlementStatus = 'pending' | 'confirmed';
export type CashSettlementMethod = 'cash' | 'cash_or_direct';

export interface RepairWork<TTimestamp = Timestamp | Date | any> {
  startedAt?: TTimestamp;
  startedByUserId?: string;
  completedAt?: TTimestamp;
  completedByUserId?: string;
  workSummary?: string;
  partsUsedNotes?: string;
  serviceNotes?: string;
}

export interface CashSettlementConfirmation<TTimestamp = Timestamp | Date | any> {
  status?: CashSettlementStatus;
  method: CashSettlementMethod;
  createdAt?: TTimestamp;
  createdByUserId?: string;
  confirmedAt?: TTimestamp;
  confirmedBy?: string;
  confirmedByUserId?: string;
  currency?: 'PLN';
  amount?: number;
  note?: string;
  settlementNote?: string;
  handoverNote?: string;
}

export type ServiceCancellationReasonCode =
  | 'out_of_scope'
  | 'no_parts'
  | 'not_economical'
  | 'capacity'
  | 'other';

export interface ServiceCancellation<TTimestamp = Timestamp | Date | any> {
  reasonCode: ServiceCancellationReasonCode;
  reasonText?: string;
  cancelledAt?: TTimestamp;
  cancelledByUserId?: string;
  cancelledByRole?: 'serviceAdmin';
}

export type RepairWorkflowV15Stage =
  | 'legacy_request_created'
  | 'legacy_quote_preparation'
  | 'legacy_quote_decision'
  | 'legacy_repair_ready'
  | 'legacy_repair_in_progress'
  | 'legacy_payment_pending'
  | 'preliminary_estimate_pending'
  | 'preliminary_estimate_decision'
  | 'awaiting_customer_delivery'
  | 'awaiting_service_pickup'
  | 'awaiting_onsite_visit'
  | 'awaiting_fulfillment_confirmation'
  | 'diagnosis_in_progress'
  | 'final_quote_decision'
  | 'repair_ready'
  | 'repair_in_progress'
  | 'cash_settlement_pending'
  | 'completed'
  | 'cancelled'
  | 'rejected'
  | 'degraded';

export type RepairWorkflowNextStepActor = 'requester' | 'service' | 'admin' | 'system';

export interface RepairWorkflowNextStep {
  actor: RepairWorkflowNextStepActor;
  kind: 'action' | 'informational' | 'terminal' | 'degraded';
  id: string;
  label: string;
}

export interface RepairWorkflowV15ReadModel {
  version: RepairWorkflowVersion;
  isLegacy: boolean;
  isV15: boolean;
  stage: RepairWorkflowV15Stage;
  stageLabel: string;
  nextStep: RepairWorkflowNextStep;
  issues: string[];
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  role: UserRole;
  status: UserStatus; // Added status field using the enum
  createdAt: Timestamp | Date | any; // Use Timestamp type from firebase/firestore
  updatedAt: Timestamp | Date | any; // Use Timestamp type
  photoURL?: string;
  securityNudges?: UserSecurityNudges<Timestamp | Date | any> | null;
}

export interface UserSecurityNudges<TTimestamp = Timestamp | Date | any> {
  totpNudgeDismissedAt?: TTimestamp | null;
  totpNudgeSnoozedUntil?: TTimestamp | null;
  totpNudgeVersion?: string | null;
}

export interface ServiceLocationInfo {
  uid: string;
  serviceName: string;
  position: [number, number]; // [lat, lng]
  averageRating?: number;
  reviewsCount?: number;
  numberOfRatings?: number;
  ratingSum?: number;
  specializations: string[];
  offeredCategories?: string[]; // <-- ADDED for Krok 4.2 filtering
  description?: string;
  address?: string;
  isOpen?: boolean;
  distance?: number;
  imageUrl?: string;
  imageStoragePath?: string;
  phoneNumber?: string;
  isVerified?: boolean;
  isTestData?: boolean;
  businessHours?: BusinessHours | null;
  availabilityOverride?: ServiceAvailabilityOverride | null;
  servicePricing?: ServiceProviderPricing<Timestamp | Date | any> | null;
  serviceFulfillment?: ServiceProviderFulfillmentConfig<Timestamp | Date | any> | null;
  serviceCapabilities?: ServiceCapabilities<Timestamp | Date | any> | null;
}

export interface ServiceProviderHourlyRate<TTimestamp = Timestamp | Date | any> {
  public: boolean;
  amount?: number;
  currency: 'PLN';
  updatedAt?: TTimestamp;
}

export interface ServiceProviderPricing<TTimestamp = Timestamp | Date | any> {
  hourlyRate?: ServiceProviderHourlyRate<TTimestamp> | null;
}

export type ServiceCapabilityIntent = 'N' | 'U' | 'D';
export type ServiceCapabilityMode = 'categoryWide' | 'selectedObjects';
export type ServiceCapabilityRiskLevel = 'low' | 'medium' | 'high';

export interface ServiceCapabilityObjectSelection {
  objectId: string;
  intents?: ServiceCapabilityIntent[];
}

export interface ServiceCapabilityItem {
  id: string;
  domainId: string;
  categoryId: string;
  mode: ServiceCapabilityMode;
  intents: ServiceCapabilityIntent[];
  objectSelections: ServiceCapabilityObjectSelection[];
  excludedObjectIds: string[];
  advancedCapabilityIds: string[];
  requiresQualification: boolean;
  qualificationType?: string | null;
  riskLevel: ServiceCapabilityRiskLevel;
}

export interface ServiceCapabilitiesIndex {
  domainIds: string[];
  categoryIds: string[];
  objectIds: string[];
  intents: ServiceCapabilityIntent[];
  advancedCapabilityIds: string[];
}

export interface ServiceCapabilities<TTimestamp = Timestamp | Date | any> {
  version: 'smart-service-graph-v1';
  updatedAt?: TTimestamp | null;
  items: ServiceCapabilityItem[];
  index: ServiceCapabilitiesIndex;
}

export interface ServiceProvider {
  uid: string;
  serviceName: string;
  description?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
  };
  location: any; // GeoPoint from Firebase
  phoneNumber: string;
  email: string;
  website?: string | null; // Allow null to match Firestore rule and avoid TS error
  businessHours?: BusinessHours | null;
  availabilityOverride?: ServiceAvailabilityOverride | null;
  servicePricing?: ServiceProviderPricing<Timestamp | Date | any> | null;
  serviceFulfillment?: ServiceProviderFulfillmentConfig<Timestamp | Date | any> | null;
  serviceCapabilities?: ServiceCapabilities<Timestamp | Date | any> | null;
  openingHours?: {
    // Legacy field. Do not use for active runtime business-hours logic.
    [key: string]: {
      open: string;
      close: string;
      isClosed: boolean;
    };
  };
  photos: string[];
  profileImage?: ServiceProviderProfileImage | null;
  specializations: string[];
  offeredCategories?: string[]; // <-- ADDED for Krok 2.3
  averageRating: number;
  reviewsCount?: number;
  numberOfRatings: number;
  ratingSum?: number;
  isVerified: boolean;
  verificationStatus?: ServiceVerificationStatus;
  verificationSubmissionId?: string | null;
  verificationReviewReason?: string | null;
  verificationReviewedAt?: Timestamp | Date | any | null;
  verificationSubmittedAt?: Timestamp | Date | any | null;
  isTestData?: boolean;
  createdAt: Timestamp | Date | any; // Timestamp
  updatedAt: Timestamp | Date | any; // Timestamp
}

export interface RepairRequest {
  uid: string;
  id?: string; // For compatibility with existing code
  clientId: string;
  serviceProviderId: string;
  serviceId?: string; // Legacy compatibility only
  subjectId?: string;
  requestIndex?: string;
  requestFlowType?: RepairRequestFlowType;
  serviceOperationalIndex?: string;
  serviceOperationalIndexSequence?: number;
  serviceOperationalIndexAssignedAt?: Timestamp | Date | any;
  manualDeviceType?: string;
  subjectSnapshot?: {
    subjectId: string;
    name: string;
    shortCode: string;
    version: number;
  };
  equipmentTypeId?: string;
  equipmentTypeSelectionType?: 'catalog' | 'other';
  equipmentTypeNameSnapshot?: string;
  equipmentTypeShortCodeSnapshot?: string;
  deviceType?: string; // Legacy compatibility only
  brand?: string;
  model?: string;
  description: string;
  status: RepairStatus;
  urgency?: 'low' | 'medium' | 'high';
  createdAt: Timestamp | Date | any; // Timestamp
  updatedAt: Timestamp | Date | any; // Timestamp
  scheduledDate?: Timestamp | Date | any; // Timestamp
  serviceSchedule?: RepairRequestServiceSchedule<Timestamp | Date | any>;
  completedDate?: Timestamp | Date | any; // Timestamp
  estimatedCost?: number;
  finalCost?: number;
  workflowVersion?: RepairWorkflowVersion;
  preliminaryEstimate?: PreliminaryEstimate<Timestamp | Date | any>;
  diagnosis?: RepairDiagnosis<Timestamp | Date | any>;
  finalQuote?: FinalRepairQuote<Timestamp | Date | any>;
  repairWork?: RepairWork<Timestamp | Date | any>;
  handoverEvents?: RepairHandoverEvent<Timestamp | Date | any>[];
  cashSettlement?: CashSettlementConfirmation<Timestamp | Date | any>;
  serviceCancellation?: ServiceCancellation<Timestamp | Date | any>;
  quotedPrice?: number;
  quoteDescription?: string;
  quotedAt?: Timestamp | Date | any;
  serviceUnreadSections?: string[];
  serviceUnreadActivityType?: string | null;
  clientUnreadSections?: string[];
  clientUnreadActivityType?: string | null;
  serviceHasPendingAction?: boolean;
  clientHasPendingAction?: boolean;
  lastServiceActivityAt?: Timestamp | Date | any;
  lastClientActivityAt?: Timestamp | Date | any;
  lastWorkflowActorUserId?: string;
  lastWorkflowActorRole?: 'serviceAdmin' | 'client' | 'service' | string;
  reviewSubmitted?: boolean;
  reviewId?: string;
  reviewSubmittedAt?: Timestamp | Date | any;
  reviewInvitationSentAt?: Timestamp | Date | any;
  photos?: string[];
  photoAssets?: RepairRequestPhotoAsset[];
  clientNotes?: string;
  serviceNotes?: string;
  priorityRequest?: RepairRequestPriorityRequest<Timestamp | Date | any>;
  fulfillmentSnapshot?: RepairRequestFulfillmentSnapshot<Timestamp | Date | any>;
  location?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  clientDetails?: {
    displayName?: string;
    email?: string;
  };
  adminArchivedAt?: Timestamp | Date | any | null;
  adminArchivedBy?: string | null;
  adminArchiveReasonCode?:
    | 'test_cleanup'
    | 'orphaned_service'
    | 'broken_legacy'
    | 'duplicate'
    | 'other';
  adminArchiveNote?: string | null;
  adminArchiveVersion?: 1;
  adminArchiveSource?: 'adminRequests';
  adminArchiveRestoredAt?: Timestamp | Date | any | null;
  adminArchiveRestoredBy?: string | null;
  adminArchiveRestoreNote?: string | null;
}

export interface RepairRequestPhotoAsset<TTimestamp = Timestamp | Date | any> {
  assetId: string;
  url: string;
  storagePath: string;
  contentType: string;
  bytes: number;
  createdAt?: TTimestamp;
  createdBy?: string;
}

export interface RequestSubject {
  uid?: string;
  id?: string;
  subjectId: string;
  name: string;
  shortCode: string;
  version: number;
  status: 'active' | 'inactive';
  description?: string;
  createdAt: Timestamp | Date | any;
  updatedAt: Timestamp | Date | any;
  deactivatedAt?: Timestamp | Date | any;
}

export type EquipmentTypeStatus = 'active' | 'inactive';

export interface EquipmentType {
  uid?: string;
  id?: string;
  equipmentTypeId: string;
  name: string;
  nameKey: string;
  shortCode: string;
  shortCodeKey: string;
  status: EquipmentTypeStatus;
  source: 'admin' | 'suggestion';
  sourceSuggestionId?: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: Timestamp | Date | any;
  updatedAt: Timestamp | Date | any;
  activatedAt?: Timestamp | Date | any | null;
  deactivatedAt?: Timestamp | Date | any | null;
}

export type EquipmentTypeSuggestionStatus = 'pending' | 'accepted' | 'rejected';

export interface EquipmentTypeSuggestion {
  uid?: string;
  id?: string;
  equipmentTypeSuggestionId: string;
  suggestedBy: string;
  suggestedByDisplayName?: string | null;
  suggestedByRole?: 'serviceAdmin' | 'serwisant' | null;
  suggestedName: string;
  suggestedNameKey: string;
  notes?: string | null;
  status: EquipmentTypeSuggestionStatus;
  source: 'serviceAdmin';
  createdAt: Timestamp | Date | any;
  updatedAt: Timestamp | Date | any;
  resolvedAt?: Timestamp | Date | any | null;
  resolvedBy?: string | null;
  canonicalEquipmentTypeId?: string | null;
  canonicalName?: string | null;
  canonicalShortCode?: string | null;
  rejectionReason?: string | null;
}

export interface SubjectSpecializationMap {
  uid?: string;
  id?: string;
  subjectId: string;
  specializationName: string;
  status: 'active' | 'inactive';
  createdAt: Timestamp | Date | any;
  updatedAt: Timestamp | Date | any;
  deactivatedAt?: Timestamp | Date | any;
}

export interface Review {
  uid?: string; // Can be auto-generated by Firestore
  id?: string; // For compatibility with existing code
  serviceId?: string; // Can be derived from serviceProvider
  serviceProviderId: string;
  clientId: string;
  requestId: string;
  repairRequestId?: string;
  rating: number;
  comment: string;
  clientDisplayName?: string;
  verified: boolean;
  isTestData?: boolean;
  requestSnapshot?: {
    deviceType?: string;
    brand?: string;
    model?: string;
  };
  text?: string; // For compatibility with existing code
  createdAt: Timestamp | Date | any; // Timestamp
  updatedAt: Timestamp | Date | any; // Timestamp
  response?: {
    text: string;
    createdAt: Timestamp | Date | any; // Timestamp
  };
}

export interface Message {
  uid: string;
  id?: string; // For compatibility with existing code
  requestId: string;
  repairRequestId?: string; // For compatibility with existing code
  senderId: string;
  receiverId?: string;
  content: string;
  text?: string; // For compatibility with existing code
  imageUrl?: string; // For future image support
  createdAt: Timestamp | Date | any; // Timestamp
  isRead: boolean;
  attachments?: {
    type: 'image' | 'file';
    url: string;
    name: string;
  }[];
}

export type SuggestionWorkflowVersion = 1 | 2;

export type SuggestionWorkflowStatus =
  | 'new'
  | 'admin_review'
  | 'voting_open'
  | 'voting_closed_accepted'
  | 'voting_closed_rejected'
  | 'declined_by_admin'
  | 'planned'
  | 'in_progress'
  | 'implemented'
  | 'archived'
  | 'in_review'
  | 'underConsideration'
  | 'accepted'
  | 'rejected';

export interface Suggestion {
  uid: string;
  id?: string; // For compatibility with existing code
  suggestionId?: string;
  workflowVersion?: SuggestionWorkflowVersion;
  userId: string;
  userDisplayName?: string; // Added for compatibility
  createdBy?: string;
  createdByDisplayName?: string | null;
  title: string;
  description: string;
  originalTitle?: string;
  originalDescription?: string;
  technicalTitle?: string | null;
  technicalSummary?: string | null;
  technicalEditedBy?: string | null;
  technicalEditedByDisplayName?: string | null;
  technicalEditedAt?: Timestamp | Date | any | null;
  category?: string;
  isArchived?: boolean;
  status: SuggestionWorkflowStatus;
  createdAt: Timestamp | Date | any; // Timestamp
  updatedAt: Timestamp | Date | any; // Timestamp
  votingOpenedAt?: Timestamp | Date | any | null;
  votingClosesAt?: Timestamp | Date | any | null;
  votingClosedAt?: Timestamp | Date | any | null;
  votingClosedBy?: string | null;
  eligibleVoterCountSnapshot?: number;
  yesCount?: number;
  noCount?: number;
  turnoutCount?: number;
  result?: 'accepted' | 'rejected' | null;
  resolutionReason?: string | null;
  adminDecisionReason?: string | null;
  archivedFromStatus?: string | null;
  archivedAt?: Timestamp | Date | any | null;
  archivedBy?: string | null;
  myVote?: 'yes' | 'no' | null;
  myVoteUpdatedAt?: Timestamp | Date | any | null;
  response?: {
    text: string;
    createdAt: Timestamp | Date | any; // Timestamp
  };
}
